import { z } from "zod";

export const aiEditFxShadowStatusSchema = z.enum([
  "QUEUED",
  "RUNNING",
  "SUCCESS",
  "CANCELLED",
  "MIGRATION_FAILED",
  "ORIGINAL_RENDER_FAILED",
  "MIGRATED_RENDER_FAILED",
  "VERSION_MISMATCH",
  "ANALYSIS_FAILED",
]);

const finiteNonNegativeNumberSchema = z.number().finite().nonnegative();
const finitePositiveNumberSchema = z.number().finite().positive();
const similaritySchema = z.number().finite().min(0).max(1);

const renderSchema = z.object({
  operationId: z.string(),
  videoAssetId: z.string().nullish(),
  error: z.string().nullish(),
});

export const aiEditFxShadowVideoMetadataSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  durationSecs: finitePositiveNumberSchema,
  frameCount: z.number().int().positive(),
  fps: finitePositiveNumberSchema,
});

export const aiEditFxShadowTimelineBucketSchema = z
  .object({
    startSecs: finiteNonNegativeNumberSchema,
    endSecs: finiteNonNegativeNumberSchema,
    timeSecs: finiteNonNegativeNumberSchema,
    index: z.number().int().nonnegative(),
    similarity: similaritySchema,
    deviation: similaritySchema,
    sampleCount: z.number().int().positive(),
  })
  .superRefine((bucket, context) => {
    if (bucket.endSecs <= bucket.startSecs) {
      context.addIssue({ code: "custom", message: "Bucket end must follow its start" });
    }
    if (Math.abs(bucket.similarity + bucket.deviation - 1) > 0.000001) {
      context.addIssue({ code: "custom", message: "Deviation must equal one minus similarity" });
    }
    if (bucket.timeSecs < bucket.startSecs || bucket.timeSecs > bucket.endSecs) {
      context.addIssue({ code: "custom", message: "Worst sample time must be inside its bucket" });
    }
  });

export const aiEditFxShadowComparisonTimelineSchema = z
  .object({
    comparedDurationSecs: finitePositiveNumberSchema,
    sampleIntervalSecs: finitePositiveNumberSchema,
    totalFrameCount: z.number().int().positive(),
    left: aiEditFxShadowVideoMetadataSchema,
    right: aiEditFxShadowVideoMetadataSchema,
    buckets: z.array(aiEditFxShadowTimelineBucketSchema).max(2000),
  })
  .superRefine((timeline, context) => {
    let previousEnd = 0;
    timeline.buckets.forEach((bucket, index) => {
      if (bucket.endSecs > timeline.comparedDurationSecs) {
        context.addIssue({
          code: "custom",
          path: ["buckets", index, "endSecs"],
          message: "Bucket exceeds compared duration",
        });
      }
      if (index > 0 && bucket.startSecs < previousEnd) {
        context.addIssue({
          code: "custom",
          path: ["buckets", index, "startSecs"],
          message: "Buckets must be ordered and non-overlapping",
        });
      }
      previousEnd = bucket.endSecs;
    });
  });

export type AiEditFxShadowComparisonTimeline = z.infer<
  typeof aiEditFxShadowComparisonTimelineSchema
>;

export const aiEditFxShadowDetailResponseSchema = z.object({
  result: z.lazy(() => aiEditFxShadowResultSchema),
  comparisonTimeline: aiEditFxShadowComparisonTimelineSchema.nullish(),
  comparisonTimelineError: z.string().nullish(),
});

export const aiEditFxShadowResultSchema = z.object({
  id: z.string(),
  editPlanId: z.string().nullish(),
  workflowNamespace: z.string().nullish(),
  workflowId: z.string(),
  workflowRunId: z.string(),
  sourceWorkflowId: z.string(),
  sourceRunId: z.string(),
  sourceProjectProtoUri: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  durationSeconds: finiteNonNegativeNumberSchema.nullish(),
  status: aiEditFxShadowStatusSchema,
  stage: z.string(),
  jerboaVersion: z.string().nullish(),
  originalProjectProtoUri: z.string().nullish(),
  migratedProjectProtoUri: z.string().nullish(),
  original: renderSchema.nullish(),
  migrated: renderSchema.nullish(),
  originalVideoUrl: z.string().url().nullish(),
  migratedVideoUrl: z.string().url().nullish(),
  comparison: z
    .object({
      similarity: z.number(),
      deviation: z.number(),
      comparedFrames: z.number(),
      comparedDurationSecs: z.number(),
      minFrameSimilarity: z.number(),
      maxFrameSimilarity: z.number(),
      artifactUri: z.string(),
      algorithm: z.string(),
    })
    .nullish(),
  error: z.string().nullish(),
  exportSettings: z.record(z.unknown()),
});

export const aiEditFxShadowResultsSchema = z.object({
  results: z.object({
    items: z.array(aiEditFxShadowResultSchema),
    pageInfo: z.object({ hasNextPage: z.boolean(), endCursor: z.string().nullable() }),
  }),
});

export const aiEditFxShadowAnalysisBucketSchema = z.object({
  bucketStart: z.number(),
  bucketEnd: z.number(),
  totalCount: z.number().int().nonnegative(),
  successCount: z.number().int().nonnegative(),
  successRate: z.number().min(0).max(1).nullish(),
  similarityCount: z.number().int().nonnegative(),
  similarityMean: z.number().finite().nullish(),
});

export type AiEditFxShadowAnalysisBucket = z.infer<typeof aiEditFxShadowAnalysisBucketSchema>;

export const aiEditFxShadowAnalysisResponseSchema = z.object({
  analysis: z.object({
    buckets: z.array(aiEditFxShadowAnalysisBucketSchema),
  }),
});
